import { useStore } from '../store/StoreContext.jsx';

// "Client has none of these" markers for a table section.
//
// The problem they solve: an advisor asks about long-term care cover, the
// client has none, and there is nowhere to put that. A blank table is
// indistinguishable from a question never asked, so the planner re-asks or
// assumes the worst. Adding an empty policy row to show it was discussed is
// worse, because that phantom record flows to Salesforce and eMoney.
//
// Deliberately per TYPE rather than per section: a client can easily hold term
// life and no LTC, so a single "no insurance" marker could not answer the
// question actually being asked (Jake plus a second advisor, via Jimmy
// 2026-08-11).
//
// A marker is mutually exclusive with the real thing: once a policy of a
// covered type exists the marker is cleared and locked, so the record can never
// say both "has an LTC policy" and "has no LTC".
export default function NoneMarkers({ sectionId, config }) {
  const { data, update } = useStore();
  const sectionData = data[sectionId] || {};
  const marked = Array.isArray(sectionData[config.key]) ? sectionData[config.key] : [];

  const rows = Array.isArray(sectionData[config.tableKey]) ? sectionData[config.tableKey] : [];
  const presentTypes = new Set(
    rows.map(r => String(r?.[config.typeColumn] ?? '').trim()).filter(Boolean)
  );

  // An option is contradicted when the table already holds a policy it covers.
  const contradicted = opt => (opt.covers || []).some(t => presentTypes.has(t));

  const toggle = opt => {
    const has = marked.includes(opt.value);
    const next = has ? marked.filter(v => v !== opt.value) : [...marked, opt.value];
    update(`${sectionId}.${config.key}`, next);
  };

  return (
    <div className="none-markers">
      <div className="none-markers-label">{config.label}</div>
      <div className="none-markers-group">
        {(config.options || []).map(opt => {
          const blocked = contradicted(opt);
          // A marker left over from before a policy was added would be a
          // contradiction on screen, so it reads as unchecked once blocked.
          const checked = !blocked && marked.includes(opt.value);
          return (
            <label
              key={opt.value}
              className={'none-marker-item' + (blocked ? ' is-blocked' : '')}
              title={
                blocked
                  ? `There is already a ${opt.value} policy in the table below.`
                  : `Record that you asked about ${opt.value} and the client has none.`
              }
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={blocked}
                onChange={() => toggle(opt)}
              />
              <span>{opt.value}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
